import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
export default function CustomerHistory() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notification, setNotification] = useState('');
  const [searchQuery, setSearchQuery] = useState(''); // State for search input
  const [selectedStatus, setSelectedStatus] = useState(''); // State for status filter
  const navigate = useNavigate();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const token = localStorage.getItem('userToken');
        if (!token) {
          throw new Error('User token not found');
        }

        const decodedToken = jwtDecode(token);
        if (decodedToken.exp < Date.now() / 1000) {
          throw new Error('Token expired');
        }

        const response = await axios.get('https://fboothapi.azurewebsites.net/api/booking', {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const sortedBookings = response.data.sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate));
        setBookings(sortedBookings);
        setLoading(false);
      } catch (err) {
        setError('Error fetching booking data');
        setLoading(false);
        navigate('/'); // Redirect to homepage if there's an error or token is missing/expired
      }
    };

    fetchBookings();
  }, [navigate]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleStatusChange = (e) => {
    setSelectedStatus(e.target.value);
  };

  const filteredBookings = bookings.filter((booking) => {
    const query = searchQuery.toLowerCase();
    const customerID = booking.customerBusinessID.toLowerCase();
    
    // Check if the customerBusinessID includes the search query or the last 5 digits match
    const matchesSearchQuery = customerID.includes(query) || customerID.slice(-5).includes(query);

    // Check if the selected status matches the booking status
    const matchesStatus = selectedStatus === '' || booking.bookingStatus === selectedStatus;

    return matchesSearchQuery && matchesStatus;
  });

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-blue-100">
        <div className="text-center">
          <div className="loader ease-linear rounded-full border-8 border-t-8 border-blue-200 h-64 w-64 mb-4"></div>
          <h2 className="text-center text-blue-600 text-xl font-semibold">
            Loading...
          </h2>
          <p className="text-blue-500">Please wait a second</p>
        </div>
      </div>
    );
  if (error) return <div>{error}</div>;

  return (
    <div className="">
      <main className="p-12">
        <div className="max-w-7xl mx-auto sm:px-2 lg:px-8">
          <div className="max-w-2xl mx-auto px-4 lg:max-w-4xl lg:px-0">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-5xl">
              Order History
            </h1>
            <div className="mt-4 flex space-x-4">
              <input
                type="text"
                placeholder="Enter booking code or last 5 digits of booking code"
                value={searchQuery}
                onChange={handleSearchChange}
                className="w-full px-4 py-2 border rounded-lg"
              />
              <select
                value={selectedStatus}
                onChange={handleStatusChange}
                className="px-4 py-2 border rounded-lg"
              >
                <option value="">All Statuses</option>
                <option value="PendingPayment">Pending Payment</option>
                <option value="TakingPhoto">Taking Photo</option>
                <option value="ExtraService">Extra Service</option>
                <option value="NoShow">No Show</option>
                <option value="Canceled">Canceled</option>
                <option value="CancelledBySystem">Cancelled by System</option>
                <option value="CompleteChecked">Complete</option>
                {/* Add more statuses as needed */}
              </select>
            </div>
          </div>
        </div>

        <section aria-labelledby="recent-heading" className="mt-12">
          <h2 id="recent-heading" className="sr-only">
            Recent orders
          </h2>
          <div className="max-w-7xl mx-auto sm:px-2 lg:px-8">
            <div className="max-w-2xl mx-auto space-y-8 sm:px-4 lg:max-w-4xl lg:px-0">
              {filteredBookings.map((booking) => (
                <div
                  key={booking.bookingID}
                  className="bg-white border border-gray-200 rounded-lg shadow-md sm:flex sm:items-center sm:justify-between sm:px-6 sm:py-6"
                >
                  <div className="flex-1">
                    <div>
                      <h3 className="text-xl text-gray-900 mr-10">
                        Booking code: <span className='text-xl font-semibold text-gray-900'>{booking.customerBusinessID}</span>
                      </h3>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-x-8 gap-y-4 sm:grid-cols-4 text-sm text-gray-500">
                      <div>
                        <p className="font-semibold text-gray-700">Status</p>
                        <p
                          className={`
                            ${
                              ['PendingPayment', 'TakingPhoto', 'ExtraService'].includes(booking.bookingStatus)
                                ? 'font-bold text-yellow-500'
                                : ['NoShow', 'Canceled', 'CancelledBySystem'].includes(booking.bookingStatus)
                                ? 'font-bold text-red-500'
                                : 'font-bold text-green-700'
                            }
                          `}
                        >
                          {booking.bookingStatus || 'N/A'}
                        </p>
                      </div>

                      <div>
                        <p className="font-semibold text-gray-700">Check-in</p>
                        <p>{booking.startTime ? new Date(booking.startTime).toLocaleString() : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Check-out</p>
                        <p>{booking.endTime ? new Date(booking.endTime).toLocaleString() : 'N/A'}</p>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-700">Total price</p>
                        <p className="font-bold text-blue-600">
                          {booking.totalPrice ? booking.totalPrice.toLocaleString() : 'N/A'} VND
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 sm:mt-0 sm:ml-4">
                    <div className="flex space-x-4">
                      <Link
                        to={`/orderdetail/${booking.bookingID}`}
                        className="flex items-center justify-center bg-white py-2 px-4 border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                      >
                        View Order
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
        <div className="pt-12 pb-24">
          <div className="text-center">
            <Link to="/profile">
              <button className="text-blue-500 py-3 px-6 uppercase rounded-lg border border-blue-500 hover:bg-blue-700 hover:text-white hover:shadow-lg font-medium transition transform hover:-translate-y-0.5">
                Back
              </button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
